import React, { useState } from "react";
import { Badge, Box, Button, Code, Collapse, HStack, Text, VStack } from "@chakra-ui/react";
import type { ApiCallRecord } from "../flow/types";
import { ACTOR_META, copyText, formatClock, pretty } from "./statusMeta";
import useNotification from "../../../utils/useNotification";

const CodeBlock: React.FC<{ title: string; value: unknown; copyable?: boolean }> = ({ title, value, copyable = true }) => {
  const toast = useNotification();
  const text = pretty(value);
  return (
    <Box>
      <HStack justify="space-between" mb={1}>
        <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.06em" color="gray.500" fontWeight="600">
          {title}
        </Text>
        {copyable && text !== "—" && (
          <Button
            size="xs"
            variant="ghost"
            h="18px"
            fontSize="10px"
            onClick={async () => {
              const ok = await copyText(text);
              toast({ status: ok ? "success" : "error", description: ok ? `${title} copied` : "Copy failed" });
            }}
          >
            Copy
          </Button>
        )}
      </HStack>
      <Code
        display="block"
        whiteSpace="pre-wrap"
        wordBreak="break-all"
        fontSize="11px"
        p={2}
        borderRadius="md"
        bg="gray.900"
        color="green.100"
        maxH="260px"
        overflowY="auto"
      >
        {text}
      </Code>
    </Box>
  );
};

const statusColor = (status?: number) =>
  !status ? "gray" : status < 300 ? "green" : status < 400 ? "yellow" : "red";

export const ApiCallRow: React.FC<{ call: ApiCallRecord; highlight?: boolean; defaultOpen?: boolean }> = ({
  call,
  highlight,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const actor = ACTOR_META[call.actor];
  return (
    <Box borderWidth="1px" borderColor={highlight ? "red.300" : "gray.200"} borderRadius="md" bg={highlight ? "red.50" : "white"}>
      <HStack px={3} py={2} spacing={3} cursor="pointer" onClick={() => setOpen((o) => !o)} _hover={{ bg: "gray.50" }}>
        <Badge colorScheme={call.method === "GET" ? "gray" : call.method === "POST" ? "blue" : "purple"} minW="52px" textAlign="center">
          {call.method}
        </Badge>
        <Text fontSize="xs" fontFamily="mono" flex={1} noOfLines={1} title={call.path}>
          {call.path}
        </Text>
        {call.isPoll && (
          <Badge variant="outline" colorScheme="orange" fontSize="9px">
            poll
          </Badge>
        )}
        <Badge colorScheme={actor.colorScheme} variant="subtle" fontSize="9px">
          {actor.label}
        </Badge>
        <Badge colorScheme={statusColor(call.status)}>{call.status ?? "ERR"}</Badge>
        <Text fontSize="10px" color="gray.500" fontFamily="mono" minW="48px" textAlign="right">
          {call.durationMs != null ? `${call.durationMs}ms` : "…"}
        </Text>
      </HStack>
      <Collapse in={open} animateOpacity>
        <VStack align="stretch" spacing={3} px={3} pb={3}>
          <HStack fontSize="10px" color="gray.500" spacing={4}>
            <Text>{call.label}</Text>
            <Text>start {formatClock(call.startedAt)}</Text>
            <Text>end {formatClock(call.endedAt)}</Text>
          </HStack>
          <CodeBlock title="cURL (tokens redacted)" value={call.curl} />
          <CodeBlock title="Endpoint" value={`${call.method} ${call.url}`} />
          <CodeBlock title="Request headers" value={call.headers} />
          <CodeBlock title="Request payload" value={call.requestBody} />
          <CodeBlock title={`Response · HTTP ${call.status ?? "ERR"}`} value={call.responseBody ?? call.errorMessage} />
          {call.errorMessage && (
            <Text fontSize="xs" color="red.600" fontWeight="600">
              {call.errorMessage}
            </Text>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
};

export default CodeBlock;
